import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, notifyError } from "../lib/api";
import { fmtMoney } from "../lib/currency";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Check, X, Loader2, Package, Star, Infinity as InfinityIcon, ArrowRight, Landmark, Phone, Mail } from "lucide-react";
import WhatsAppIcon from "../components/WhatsAppIcon";

const KDV_RATE = 0.2;

export default function Subscription() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [confirmPlan, setConfirmPlan] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        api.get("/billing/my-subscription"),
        api.get("/billing/plans"),
      ]);
      setMe(s.data);
      setPlans(p.data);
    } catch (e) {
      notifyError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Polling after Stripe success
  useEffect(() => {
    const sid = params.get("session_id");
    const cancelled = params.get("cancelled");
    const free = params.get("free");
    if (free) {
      toast.success("Ücretsiz paket aktifleştirildi");
      const np = new URLSearchParams(params); np.delete("free"); setParams(np);
      load();
      return;
    }
    if (cancelled) {
      toast.info("Ödeme iptal edildi");
      const np = new URLSearchParams(params); np.delete("cancelled"); setParams(np);
      return;
    }
    if (!sid) return;
    let cancelToken = false;
    let attempts = 0;
    const max = 8;
    const poll = async () => {
      if (cancelToken || attempts >= max) return;
      attempts++;
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        if (data.payment_status === "paid") {
          toast.success("Ödeme alındı, paket aktif");
          const np = new URLSearchParams(params); np.delete("session_id"); setParams(np);
          load();
          return;
        }
        if (data.status === "expired") {
          toast.error("Ödeme süresi doldu");
          return;
        }
      } catch (e) {
        // continue
      }
      setTimeout(poll, 2500);
    };
    poll();
    return () => { cancelToken = true; };
    // eslint-disable-next-line
  }, []);

  // "Seç" tıklaması: ücretli paketlerde KDV kırılımını gösteren onay modalı
  // açılır; ücretsiz pakette doğrudan aktifleştirme yapılır.
  const handleSelect = (plan) => {
    if (plan.price_try > 0) {
      setConfirmPlan(plan);
    } else {
      startCheckout(plan);
    }
  };

  const proceedToPayment = async () => {
    const plan = confirmPlan;
    setConfirmPlan(null);
    if (plan) await startCheckout(plan);
  };

  const startCheckout = async (plan) => {
    setBusy(plan.id);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan_id: plan.id,
        // Omit provider — backend will use master-configured default_provider
        origin_url: window.location.origin,
      });
      if (data.provider === "free") {
        toast.success("Ücretsiz paket aktifleştirildi");
        await load();
        return;
      }
      if (data.provider === "paytr") {
        // Backend chooses: same-origin /odeme/paytr-mock when test mode is on,
        // else external https://www.paytr.com/odeme/guvenli/{token}.
        try {
          const u = new URL(data.url);
          if (u.origin === window.location.origin) {
            const sep = u.search ? "&" : "?";
            navigate(
              `${u.pathname}${u.search}${sep}plan=${encodeURIComponent(plan.name)}&amount=${plan.price_try}`
            );
          } else {
            window.location.href = data.url;
          }
        } catch {
          window.location.href = data.url;
        }
        return;
      }
      // Stripe — external redirect
      window.location.href = data.url;
    } catch (err) {
      notifyError(err);
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <div className="p-8 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto space-y-6" data-testid="subscription-page">
      <div>
        <div className="label-eyebrow text-amber-600">Abonelik</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-2">Paketim</h1>
        <p className="text-sm text-muted-foreground mt-2">İşletmenizin paketi ve yükseltme seçenekleri.</p>
      </div>

      {/* IBAN payment info */}
      <Card
        className="rounded-sm border-amber-500/50 bg-amber-50/60 dark:bg-amber-950/20 p-5"
        data-testid="iban-info"
      >
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 bg-amber-500 text-foreground rounded-sm flex items-center justify-center shrink-0">
            <Landmark className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <div className="font-display font-bold text-base tracking-tight">
              IBAN ile Ödeme
            </div>
            <p className="text-sm text-foreground/80 mt-1">
              IBAN ile ödeme yapmak için{" "}
              <a
                href="tel:+905317932454"
                className="font-semibold underline hover:text-amber-700"
              >
                <Phone className="inline h-3.5 w-3.5 mr-0.5 align-[-2px]" />
                +90 531 793 24 54
              </a>{" "}
              <a
                href="https://wa.me/905317932454"
                target="_blank"
                rel="noopener noreferrer"
                title="WhatsApp ile yaz"
                className="font-semibold underline text-emerald-700 hover:text-emerald-800"
              >
                <WhatsAppIcon className="inline h-3.5 w-3.5 mr-0.5 align-[-2px]" />
                WhatsApp
              </a>{" "}
              numaralı telefondan veya{" "}
              <a
                href="mailto:info@esnekyazilim.com.tr"
                className="font-semibold underline hover:text-amber-700"
              >
                <Mail className="inline h-3.5 w-3.5 mr-0.5 align-[-2px]" />
                info@esnekyazilim.com.tr
              </a>{" "}
              adresi ile iletişime geçerek bilgi alabilirsiniz.
            </p>
          </div>
        </div>
      </Card>

      {/* Current subscription */}
      <Card className="rounded-sm border-border bg-foreground text-background p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="label-eyebrow text-amber-400">Aktif Paket</div>
            <div className="font-display font-black text-3xl tracking-tighter mt-2">
              {me?.plan?.name || "Yok"}
            </div>
            <div className="text-sm text-background/70 mt-2 flex flex-wrap gap-4">
              {me?.plan && (
                <>
                  <span>Müşteri: <strong>{me.plan.max_customers > 0 ? me.plan.max_customers : "Sınırsız"}</strong></span>
                  <span>Kullanıcı: <strong>{me.plan.max_users > 0 ? me.plan.max_users : "Sınırsız"}</strong></span>
                  <span data-testid="active-plan-sales-stock">
                    Satış/Stok:{" "}
                    <strong className={me.plan.sales_stock_enabled ? "text-amber-400" : ""}>
                      {me.plan.sales_stock_enabled ? "Dahil" : "Dahil değil"}
                    </strong>
                  </span>
                </>
              )}
              {me?.expires_at && (
                <span>Bitiş: <strong>{new Date(me.expires_at).toLocaleDateString("tr-TR")}</strong></span>
              )}
              {!me?.expires_at && me?.plan && (
                <span className="text-amber-400 font-semibold">Sınırsız süre</span>
              )}
            </div>
          </div>
          <Badge className={`rounded-sm ${me?.status === "active" ? "bg-amber-500 text-foreground hover:bg-amber-500" : "bg-background/20 text-background hover:bg-background/20"}`}>
            {me?.status === "active" ? "AKTİF" : "PASİF"}
          </Badge>
        </div>
      </Card>

      {/* Available plans */}
      <div>
        <h2 className="font-display font-bold text-2xl tracking-tight">Paketler</h2>
        <p className="text-sm text-muted-foreground mt-2 mb-4" data-testid="plans-intro">
          Aşağıdaki paketlerden işletmenize uygun olanı kredi kartınız ile temin
          edebilirsiniz.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((p) => {
            const current = me?.plan_id === p.id;
            const isFreePlan = !(p.price_try > 0);
            const blockedFree = isFreePlan && me?.has_paid_history;
            return (
              <Card
                key={p.id}
                className={`rounded-sm p-6 border ${current ? "border-amber-500 bg-amber-500/5" : "border-border"}`}
                data-testid={`subscribe-plan-${p.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Package className="h-4 w-4 text-amber-600" />
                    {p.is_default && <Badge variant="outline" className="rounded-sm text-[10px]"><Star className="h-3 w-3 mr-1" /> Free</Badge>}
                    {current && <Badge className="rounded-sm bg-amber-500 text-foreground hover:bg-amber-500 text-[10px]">Aktif</Badge>}
                  </div>
                </div>
                <div className="font-display font-black text-2xl tracking-tighter mt-3">{p.name}</div>
                <div className="font-display font-black text-3xl tracking-tighter mt-1">
                  {p.price_try > 0 ? fmtMoney(p.price_try, p.currency || "TRY", { maximumFractionDigits: 0 }) : <span className="text-amber-600">Ücretsiz</span>}
                  {p.price_try > 0 && p.period_days > 0 && (
                    <span className="text-sm text-muted-foreground ml-1 font-normal">/{p.period_days}gün</span>
                  )}
                </div>
                <ul className="mt-4 space-y-2 text-sm border-t border-border pt-4">
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-amber-600 shrink-0" />
                    <span>{p.max_customers > 0 ? `${p.max_customers} müşteri` : <span className="inline-flex items-center gap-1"><InfinityIcon className="h-3 w-3" /> Sınırsız müşteri</span>}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-amber-600 shrink-0" />
                    <span>{p.max_users > 0 ? `${p.max_users} kullanıcı` : <span className="inline-flex items-center gap-1"><InfinityIcon className="h-3 w-3" /> Sınırsız kullanıcı</span>}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-amber-600 shrink-0" />
                    <span>{p.period_days > 0 ? `${p.period_days} gün` : "Sınırsız süre"}</span>
                  </li>
                  {/* Satış/Stok modülü pakete dahil mi (şartname #10). Kapalı
                      paketlerde de gösterilir — kullanıcı paketler arasındaki
                      farkı görebilsin. */}
                  <li
                    className={`flex items-center gap-2 ${p.sales_stock_enabled ? "" : "text-muted-foreground"}`}
                    data-testid={`plan-sales-stock-${p.id}`}
                  >
                    {p.sales_stock_enabled ? (
                      <Check className="h-4 w-4 text-amber-600 shrink-0" />
                    ) : (
                      <X className="h-4 w-4 shrink-0 opacity-60" />
                    )}
                    <span>
                      Satış / Stok modülü
                      {p.sales_stock_enabled ? "" : " (dahil değil)"}
                    </span>
                  </li>
                </ul>
                <Button
                  onClick={() => handleSelect(p)}
                  disabled={current || busy === p.id || blockedFree}
                  className="w-full mt-5 rounded-sm bg-foreground text-background hover:bg-foreground/90"
                  data-testid={`checkout-plan-${p.id}`}
                  title={blockedFree ? "Ücretsiz Paket Satın Alınamaz" : undefined}
                >
                  {busy === p.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : current ? (
                    "Mevcut Paket"
                  ) : blockedFree ? (
                    "Ücretsiz Paket Satın Alınamaz"
                  ) : (
                    <>Seç <ArrowRight className="h-4 w-4 ml-1" /></>
                  )}
                </Button>
              </Card>
            );
          })}
        </div>
      </div>

      {/* KDV kırılımı onay modalı */}
      <Dialog open={!!confirmPlan} onOpenChange={(o) => { if (!o) setConfirmPlan(null); }}>
        <DialogContent className="rounded-sm max-w-md" data-testid="kdv-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Ödeme Özeti</DialogTitle>
          </DialogHeader>
          {confirmPlan && (() => {
            const cur = confirmPlan.currency || "TRY";
            const net = confirmPlan.price_try || 0;
            const kdv = net * KDV_RATE;
            const gross = net + kdv;
            return (
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Paket</span>
                  <span className="font-semibold">{confirmPlan.name}</span>
                </div>
                <div className="border-y border-border py-3 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Paket Tutarı</span>
                    <span className="font-semibold" data-testid="kdv-net">{fmtMoney(net, cur, { maximumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">KDV (%20)</span>
                    <span className="font-semibold" data-testid="kdv-amount">{fmtMoney(kdv, cur, { maximumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-border">
                    <span className="text-sm font-semibold">Genel Toplam</span>
                    <span className="font-display font-black text-xl tracking-tighter" data-testid="kdv-gross">
                      {fmtMoney(gross, cur, { maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Fiyatlara KDV (%20) dahil değildir; ödemede KDV dahil toplam tahsil edilir.</p>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmPlan(null)} className="rounded-sm">
              İptal
            </Button>
            <Button
              onClick={proceedToPayment}
              disabled={!!busy}
              className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
              data-testid="kdv-proceed-btn"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Ödemeye Geç <ArrowRight className="h-4 w-4 ml-1" /></>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
